/*
 * The Foozie: "Notify me when available" on a sold-out variant (session A3,
 * the front end of the BIS session's market-aware back-in-stock watcher).
 *
 * WHY AN EVENT AND NOT KLAVIYO'S NATIVE BACK IN STOCK: a native subscription
 * cannot carry the MARKET, and Klaviyo's native BIS counts total inventory, so
 * a UK request would fire off Australian stock. The watcher
 * (workspace-automation, mac/lib/fz_bis_watch.py) reads the "Back in Stock
 * Requested" event this form sends, checks stock only at the warehouse serving
 * the shopper's market (us a2b, ca Toronto, gb/eu 3PLUK, au/nz DBG) and sends
 * the email itself. So /client/back-in-stock-subscriptions is never called,
 * not even as a fallback.
 *
 * WHAT IS SENT is the contract the watcher parses, and nothing else: no URL,
 * image or price. The Client API is public, so a forged request must not be
 * able to put a link in our email; the watcher rebuilds the title, the image
 * and the link (on the market's own domain) from the Admin API.
 *
 * CONSENT: the shopper asks for this one email and nothing is stored on the
 * device, so the request is not gated on Shopify.customerPrivacy (the
 * orchestrator's D1). The newsletter box starts UNTICKED and is the only
 * marketing consent; the list subscription is sent only when it is ticked,
 * and only after the event was accepted.
 *
 * The top of this file is pure functions with no DOM and no imports, so
 * bin/test-fz-bis.mjs can import it under Node. The element is defined only in
 * a browser.
 */

export const KLAVIYO_CLIENT = "https://a.klaviyo.com/client";
/** The revision A5c's poll checked its shapes against; the watcher expects the same. */
export const KLAVIYO_REVISION = "2026-07-15";
export const METRIC = "Back in Stock Requested";
export const SCHEMA = 1;
export const SOURCE = "fz-bis";
/** localStorage key: "1" marks every request from this browser as a test (A5c's fz_poll_test). */
export const TEST_KEY = "fz_bis_test";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DIGITS = /^\d+$/;

/** @param {unknown} value */
export function isValidEmail(value) {
  return typeof value === "string" && EMAIL_PATTERN.test(value.trim());
}

/**
 * True only when this browser has been marked for testing. localStorage throws
 * in some in-app browsers and private windows, and the GETTER itself can throw
 * (window.localStorage, before any getItem), so pass a function that reads it:
 * isTest(() => window.localStorage). A throw anywhere means "not a test", and
 * the request still goes (A7's IA-07).
 * @param {Storage | (() => Storage | null | undefined) | undefined | null} storage
 */
export function isTest(storage) {
  try {
    const store = typeof storage === "function" ? storage() : storage;
    return store?.getItem(TEST_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * One request per shopper, market, variant and UTC day: Klaviyo records a
 * repeated unique_id for the same profile and metric once.
 * @param {string} market
 * @param {string} variantId
 * @param {Date} [now]
 */
export function uniqueId(market, variantId, now = new Date()) {
  return `bis:${market}:${variantId}:${now.toISOString().slice(0, 10)}`;
}

function headers() {
  return {
    "content-type": "application/vnd.api+json",
    accept: "application/vnd.api+json",
    revision: KLAVIYO_REVISION,
  };
}

/**
 * @param {string} path
 * @param {string} companyId
 */
function clientUrl(path, companyId) {
  if (!companyId) throw new TypeError("fz-bis: a Klaviyo public key (company_id) is required");
  return `${KLAVIYO_CLIENT}/${path}/?company_id=${encodeURIComponent(companyId)}`;
}

/**
 * POST /client/events/: the "Back in Stock Requested" event the watcher reads.
 * variant_id and product_id travel as STRINGS of digits (a JSON number would
 * lose precision past 2^53, and the watcher compares strings).
 * @param {{
 *   companyId: string, email: string, variantId: string | number, productId: string | number,
 *   productHandle: string, productTitle?: string, variantTitle?: string, market: string,
 *   country?: string, domain?: string, optIn?: boolean, test?: boolean, now?: Date,
 * }} input
 */
export function buildEventRequest({
  companyId,
  email,
  variantId,
  productId,
  productHandle,
  productTitle = "",
  variantTitle = "",
  market,
  country = "",
  domain = "",
  optIn = false,
  test = false,
  now = new Date(),
}) {
  if (!isValidEmail(email)) throw new TypeError("fz-bis: a valid email is required");
  const variant = String(variantId ?? "");
  const product = String(productId ?? "");
  if (!DIGITS.test(variant)) throw new TypeError("fz-bis: variant_id must be a Shopify variant id");
  if (!DIGITS.test(product)) throw new TypeError("fz-bis: product_id must be a Shopify product id");
  if (!productHandle) throw new TypeError("fz-bis: product_handle is required");
  if (!market) throw new TypeError("fz-bis: market is required");
  const body = {
    data: {
      type: "event",
      attributes: {
        metric: { data: { type: "metric", attributes: { name: METRIC } } },
        profile: {
          data: {
            type: "profile",
            attributes: { email: email.trim(), properties: { fz_bis_market: market } },
          },
        },
        properties: {
          schema: SCHEMA,
          source: SOURCE,
          variant_id: variant,
          product_id: product,
          product_handle: productHandle,
          product_title: productTitle,
          variant_title: variantTitle,
          market,
          country,
          domain,
          marketing_opt_in: optIn === true,
          theme_arm: "variant",
          test: test === true,
        },
        unique_id: uniqueId(market, variant, now),
      },
    },
  };
  return {
    url: clientUrl("events", companyId),
    init: { method: "POST", headers: headers(), body: JSON.stringify(body) },
  };
}

/**
 * POST /client/subscriptions/: the newsletter, ONLY when the box is ticked
 * (A5c's buildSubscribeRequest, with the market as the source).
 * @param {{ companyId: string, listId: string, email: string, market: string }} input
 */
export function buildSubscribeRequest({ companyId, listId, email, market }) {
  if (!listId) throw new TypeError("fz-bis: a Klaviyo list id is required to subscribe");
  if (!isValidEmail(email)) throw new TypeError("fz-bis: a valid email is required to subscribe");
  if (!market) throw new TypeError("fz-bis: market is required");
  const body = {
    data: {
      type: "subscription",
      attributes: {
        custom_source: `${SOURCE}: ${market}`,
        profile: {
          data: {
            type: "profile",
            attributes: {
              email: email.trim(),
              properties: { fz_bis_market: market },
              subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
            },
          },
        },
      },
      relationships: { list: { data: { type: "list", id: listId } } },
    },
  };
  return {
    url: clientUrl("subscriptions", companyId),
    init: { method: "POST", headers: headers(), body: JSON.stringify(body) },
  };
}

/**
 * Klaviyo answers 202 with an empty body. Any 2xx is done; a 4xx, a 429, a
 * 5xx or a network failure (status 0) shows the error line and lets the
 * shopper try again.
 * @param {number} status
 */
export function outcome(status) {
  return status >= 200 && status < 300 ? "ok" : "error";
}

/**
 * Submit the form: the event, then (ticked box only) the subscription.
 * `fetchImpl` is injectable so the seam can be tested without a network.
 * Resolves 'ok' or 'error'; a failed subscription after an accepted event is
 * still 'ok', because the shopper's request for this email was recorded.
 * @param {Parameters<typeof buildEventRequest>[0] & { listId?: string }} input
 * @param {typeof fetch} [fetchImpl]
 */
export async function submit(input, fetchImpl = globalThis.fetch) {
  const event = buildEventRequest(input);
  let status = 0;
  try {
    status = (await fetchImpl(event.url, event.init)).status;
  } catch {
    status = 0;
  }
  if (outcome(status) !== "ok") return "error";
  if (input.optIn === true && input.listId) {
    const subscribe = buildSubscribeRequest({
      companyId: input.companyId,
      listId: input.listId,
      email: input.email,
      market: input.market,
    });
    try {
      await fetchImpl(subscribe.url, subscribe.init);
    } catch {
      /* The back-in-stock request stands; the newsletter can be joined later. */
    }
  }
  return "ok";
}

/* ------------------------------------------------------------ the element */

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  /**
   * <fz-notify>: rendered by blocks/fz-buy-box-notify.liquid for the variant
   * the page was rendered for, and re-rendered (fz-sync) on every variant
   * change, so its data attributes ARE the variant selected at submit time.
   */
  class FzNotify extends HTMLElement {
    connectedCallback() {
      this.addEventListener("submit", this.#onSubmit);
    }

    disconnectedCallback() {
      this.removeEventListener("submit", this.#onSubmit);
    }

    /** @param {SubmitEvent} event */
    #onSubmit = async (event) => {
      const form = /** @type {HTMLFormElement} */ (event.target);
      if (!form.matches(".fz-notify__form")) return;
      event.preventDefault();
      const status = this.querySelector(".fz-notify__status");
      const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
      const email = /** @type {HTMLInputElement} */ (form.elements.namedItem("email")).value;
      const box = /** @type {HTMLInputElement | null} */ (form.elements.namedItem("newsletter"));
      const data = this.dataset;
      if (button) button.disabled = true;
      let result = "error";
      try {
        result = await submit({
          companyId: data.company || "",
          listId: data.list || "",
          email,
          variantId: data.variantId || "",
          productId: data.productId || "",
          productHandle: data.productHandle || "",
          productTitle: data.productTitle || "",
          variantTitle: data.variantTitle || "",
          market: data.market || "",
          country: data.country || "",
          domain: data.domain || window.location.hostname,
          optIn: box?.checked === true,
          test: isTest(() => window.localStorage),
        });
      } catch {
        result = "error";
      }
      this.dataset.state = result === "ok" ? "done" : "error";
      if (status) status.textContent = (result === "ok" ? data.done : data.error) || "";
      if (button && result !== "ok") button.disabled = false;
    };
  }

  if (!customElements.get("fz-notify")) customElements.define("fz-notify", FzNotify);
}
